import React, { useRef, useLayoutEffect, useCallback } from 'react';
import { Edit2, Trash2, EyeOff, Eye, Paperclip, GripVertical } from 'lucide-react';
import { INSTRUCTION_BUILDER_TAB_COLORS } from '../../../helpers/constants';

const InstructionStepItem = ({
  step,
  stepNumber,
  provided,
  snapshot,
  onEdit,
  onDelete,
  onHide,
  onManageFiles,
  isHeader
}) => {
  const tabColor = INSTRUCTION_BUILDER_TAB_COLORS[step.tab] || { bg: '#f3f4f6', text: '#6b7280' };
  const isHidden = step.isHidden;
  const canManageFiles = step._id && !String(step._id).startsWith('temp-');

  const rowRef = useRef(null);
  const cellRefs = useRef([]);
  const cellWidthsRef = useRef([]);

  // Before each drag, snapshot the exact pixel width of every cell so that
  // when the row is lifted out of the table context the columns stay locked.
  useLayoutEffect(() => {
    if (!snapshot.isDragging) {
      cellWidthsRef.current = cellRefs.current.map(cell =>
        cell ? cell.getBoundingClientRect().width : undefined
      );
    }
  });

  const setRef = useCallback(
    node => {
      rowRef.current = node;
      provided.innerRef(node);
    },
    [provided.innerRef]
  );

  if (isHeader) {
    return (
      <tr className="bg-light border-bottom" style={{ borderBottomWidth: '2px' }}>
        {[
          { header: '#', key: 'stepNumber' },
          { header: 'TAB', key: 'tab' },
          { header: 'SCOPE', key: 'scope' },
          { header: 'INSTRUCTION', key: 'instruction' },
          { header: 'ACTIONS', key: 'actions' }
        ].map(column => (
          <th
            key={column.key}
            className={`py-2 px-3 font-weight-bold text-muted text-uppercase smallFont${
              column.key === 'scope' ? ' text-nowrap' : ''
            }`}
          >
            {column.header}
          </th>
        ))}
      </tr>
    );
  }

  const getRowStyle = draggableStyle => {
    if (snapshot.isDragging) {
      return {
        ...draggableStyle,
        display: 'table',
        boxShadow: '0 8px 20px rgba(0,0,0,0.15)',
        background: '#f9fafb',
        borderRadius: '8px',
        overflow: 'hidden'
      };
    }

    return {
      ...draggableStyle,
      background: 'white',
      borderBottom: '1px solid #e5e7eb',
      opacity: isHidden ? 0.6 : 1,
      transition: 'box-shadow 0.2s, opacity 0.2s, background-color 0.15s',
      ...(isHidden && {
        backgroundImage:
          'repeating-linear-gradient(135deg, transparent, transparent 8px, rgba(229,231,235,0.4) 8px, rgba(229,231,235,0.4) 16px)'
      })
    };
  };

  // Lock each cell to its pre-drag measured pixel width so columns stay
  // consistent when the row is outside the table's layout context.
  const getCellStyle = (cellIndex, extraStyle = {}) => ({
    ...extraStyle,
    ...(snapshot.isDragging && {
      borderBottom: 'none',
      ...(cellWidthsRef.current[cellIndex] !== undefined && {
        width: `${cellWidthsRef.current[cellIndex]}px`,
        minWidth: `${cellWidthsRef.current[cellIndex]}px`,
        maxWidth: `${cellWidthsRef.current[cellIndex]}px`
      })
    })
  });

  return (
    <tr ref={setRef} {...provided.draggableProps} style={getRowStyle(provided.draggableProps.style)}>
      <td ref={cell => (cellRefs.current[0] = cell)} className="py-3 px-3" style={getCellStyle(0)}>
        <div className="d-flex align-items-center" style={{ gap: '8px' }}>
          {!isHidden && (
            <div
              {...provided.dragHandleProps}
              className="text-muted"
              style={{ cursor: 'grab', lineHeight: 1, userSelect: 'none' }}
            >
              <GripVertical size={16} />
            </div>
          )}
          <span className="font-weight-bold midFont text-dark">{stepNumber}</span>
        </div>
      </td>

      <td ref={cell => (cellRefs.current[1] = cell)} className="py-3 px-3" style={getCellStyle(1)}>
        <span
          className="font-weight-bold text-uppercase xsmallFont d-inline-block text-nowrap rounded-pill px-3"
          style={{
            background: tabColor.bg,
            color: tabColor.text
          }}
        >
          {step?.tab || ''}
        </span>
      </td>

      <td ref={cell => (cellRefs.current[2] = cell)} className="py-3 px-3 text-nowrap" style={getCellStyle(2)}>
        <span
          className="font-weight-bold smallFont d-inline-block text-nowrap rounded-pill px-3"
          style={{
            background: step?.scope === 'county' ? 'var(--info-light)' : 'var(--success-light)',
            color: step?.scope === 'county' ? 'var(--info)' : 'var(--success)',
            border: `1px solid ${step?.scope === 'county' ? 'var(--info)' : 'var(--success)'}`
          }}
        >
          {step?.scope.charAt(0).toUpperCase() + step?.scope.slice(1)}
        </span>
      </td>

      <td ref={cell => (cellRefs.current[3] = cell)} className="py-3 px-3" style={getCellStyle(3)}>
        {step?.title && <h6 className="font-weight-bold text-dark mb-1 midFont">{step?.title || ''}</h6>}

        {step?.description && <p className="text-muted smallFont mb-2 text-clamp-1">{step?.description || ''}</p>}

        <button
          type="button"
          onClick={() => onManageFiles(step)}
          disabled={!canManageFiles}
          title={!canManageFiles ? 'Save step first to attach files' : 'Manage attachments'}
          className="mt-2 btn btn-sm hover-success-light text-success border-success font-weight-bold shadow-none d-flex align-items-center justify-content-center rounded-pill"
          style={{
            gap: '4px'
          }}
        >
          <Paperclip size={14} />
          <span>Manage Attachments ({step?.files?.length || 0})</span>
        </button>
      </td>

      <td ref={cell => (cellRefs.current[4] = cell)} className="py-3 px-3" style={getCellStyle(4)}>
        <div className="d-flex align-items-center" style={{ gap: '5px' }}>
          <button
            type="button"
            onClick={() => onEdit(step)}
            className="btn btn-sm d-flex align-items-center justify-content-center p-2 hover-muted border-0 shadow-none"
            title="Edit step"
          >
            <Edit2 size={16} color="var(--info)" />
          </button>
          <button
            type="button"
            onClick={() => onDelete(step)}
            className="btn btn-sm d-flex align-items-center justify-content-center p-2 hover-muted border-0 shadow-none text-danger"
            title="Delete step"
          >
            <Trash2 size={16} />
          </button>
          {step?.scope === 'county' && canManageFiles && (
            <button
              type="button"
              onClick={() => onHide(step)}
              className="btn btn-sm d-flex align-items-center justify-content-center p-2 hover-muted border-0 shadow-none text-success"
              title={isHidden ? 'Unhide step' : 'Hide step for this municipality'}
            >
              {isHidden ? <Eye size={16} /> : <EyeOff size={16} />}
            </button>
          )}
        </div>
      </td>
    </tr>
  );
};

export default InstructionStepItem;
